import { use100vh } from "react-div-100vh";
import Nav from "@/components/common/Nav";
import PageHead from "@/components/common/PageHead";
import { Button } from "@mantine/core";
import useAccount from "@/hooks/useAccount";
import useRequest, { disableRevalidate } from "@/hooks/useRequest";
import { useCallback, useEffect, useState } from "react";
import NetworkMenu from "@/components/network/NetworkMenu";
import AirdropDetail from "@/components/network/airdrop/AirdropDetail";
import { GetAirdropInfoResponse } from "@/types/network";
import useUserAirdrop from "@/hooks/airdrop/useUserAirdrop";
import { KATAmountFormat } from "@/utils/format";
import NotAvailableTip from "@/components/network/NotAvaliableTip";
import dayjs from "dayjs";
import store, { useTypedSelector, actions } from "@/stores";
import _ from "lodash";

function parseAmount(amount?: string | number) {
  if (!amount) {
    return 0;
  }
  if (amount === "-" || amount === "--") {
    return 0;
  }
  if (typeof amount === "string") {
    return parseFloat(amount);
  }
  return amount;
}

function processAirdropInfo(airdropInfo?: GetAirdropInfoResponse) {
  const airdrop1 = {
    parts: [
      {
        label: "Claimer",
        desc: "The reward of your wallet quality",
        score: KATAmountFormat(airdropInfo?.A1_Claimer_Score),
        tokens: KATAmountFormat(airdropInfo?.A1_Claimer_Tokens),
      },
      {
        label: "Validator",
        desc: "The reward of your validator node",
        score: KATAmountFormat(airdropInfo?.A1_Validator_Score),
        tokens: KATAmountFormat(airdropInfo?.A1_Validator_Tokens),
      },
      {
        label: "Lieutenant",
        desc: "Your contribution to other validator nodes",
        score: KATAmountFormat(airdropInfo?.A1_Lieutenant_Score),
        tokens: KATAmountFormat(airdropInfo?.A1_Lieutenant_Tokens),
      },
      {
        label: "Early Supporter",
        desc: "Reward to validator #30-107",
        score: "--",
        tokens: KATAmountFormat(airdropInfo?.A1_Early_Supporter_Tokens),
      },
    ],
    name: "Airdrop #1",
    claimTokens: KATAmountFormat(airdropInfo?.A1_Total_Tokens),
    locked: false,
    rule: "https://medium.com/@karatdao/karatdao-airdrop-update-3c74343fdf01",
  };

  const airdrop2 = {
    parts: [
      {
        label: "Claimer",
        desc: "The reward of your wallet quality",
        score: KATAmountFormat(airdropInfo?.A2_Claimer_Score),
        tokens: KATAmountFormat(airdropInfo?.A2_Claimer_Tokens_unlocked),
      },
      {
        label: "Lieutenant",
        desc: "Your contribution to other validator nodes",
        score: KATAmountFormat(airdropInfo?.A2_Lieutenant_Score),
        tokens: KATAmountFormat(airdropInfo?.A2_Lieutenant_Tokens),
      },
      {
        label: "VC",
        desc: "First 100,000 VC Holder will be eligible for this",
        score: KATAmountFormat(airdropInfo?.A2_VC_Score),
        tokens: KATAmountFormat(airdropInfo?.A2_VC_Tokens),
      },
    ],
    name: "Airdrop #2",
    rule: "https://medium.com/@karatdao/2nd-round-karat-airdrop-rules-97f231de99b1",
    claimTokens: KATAmountFormat(airdropInfo?.A2_Total_Tokens),
    locked: false,
  };

  const validatorAirdrop = {
    parts: [] as any,
    name: "Validator reward from airdrop #2",
    claimTokens: KATAmountFormat(
      airdropInfo?.A2ValidatorAirdrop?.claimableAmount
    ),
    locked: false,
    tip: "Reward unlocks when 35,000 KAT are staked into your validator",
  };

  airdropInfo?.A2ValidatorAirdrop?.validators?.forEach((validator: any) => {
    validatorAirdrop.parts.push({
      label: `Validator #${validator.tokenId}`,
      desc: " ",
      score: KATAmountFormat(validator.score),
      tokens: KATAmountFormat(validator.tokenAmount),
    });
  });

  return {
    airdrop1,
    airdrop2,
    validatorAirdrop,
  };
}

const airdropOpenDate = new Date(
  process.env.NEXT_PUBLIC_AIRDROP_OPEN_DATE as string
);

const AirdropPage = () => {
  const height = use100vh();
  const { login, address, isLogin } = useAccount();
  const [airdrops, setAirdrops] = useState(processAirdropInfo());
  const {
    basicClaim,
    basicClaimLoading,
    validatorClaim,
    validatorClaimLoading,
    basicClaimed,
    validatorClaimed,
  } = useUserAirdrop();

  const canAccess = isLogin && address;
  const airdropRetweetDate = useTypedSelector((state) => {
    return state.app.airdropRetweetDate;
  });

  const showRetweet =
    !airdropRetweetDate ||
    Date.now() - new Date(airdropRetweetDate).getTime() > 60 * 1000 * 60 * 24;

  const {
    data: airdropInfo,
    mutate,
    isLoading,
  } = useRequest<GetAirdropInfoResponse>(
    canAccess
      ? {
          function: "network/getAirdropInfo",
          data: {
            address,
          },
          header: {
            endpoint: "network",
          },
        }
      : null,
    {
      ...disableRevalidate,
    }
  );

  useEffect(() => {
    setAirdrops(processAirdropInfo(airdropInfo));
  }, [airdropInfo]);

  const onClaim = async () => {
    if (basicClaimLoading || basicClaimed) {
      return;
    }
    await basicClaim();
    mutate();
  };

  const onRetweet = useCallback(() => {
    store.dispatch(actions.app.showAirdropRetweet());
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `Karat to the MOON!! I just received ${
          airdropInfo?.basicClaimableAmount || 0
        } $KAT from @KaratDAO Alpha Mining. Start joining the Karat Mainnet and get a head start to 10X more rewards. #KAT #KaratDAO #socialfisummer`
      )}`,
      "_blank"
    );
  }, [airdropInfo]);

  const hasAirdrop = !!airdropInfo?.Owner || isLoading;
  const hasValidatorAirdrop =
    !!airdropInfo?.A2ValidatorAirdrop?.validators?.length;
  const totalAmount =
    parseAmount(airdropInfo?.A1_Total_Tokens) +
    parseAmount(airdropInfo?.A2_Total_Tokens) +
    parseAmount(airdropInfo?.A2_Validator_Tokens) +
    parseAmount(airdropInfo?.A2_Claimer_Tokens_locked);

  const claimable = new Date() >= airdropOpenDate;

  const btns = _.shuffle([
    <Button
      variant="outline"
      radius="xl"
      className={`w-[215px] h-[44px] mt-4 !bg-white`}
      loading={basicClaimLoading}
      onClick={onClaim}
      key={(Math.random() + 1).toString(36).substring(7)}
    >
      Claim
    </Button>,
    <Button
      variant="outline"
      radius="xl"
      className={`w-[215px] h-[44px] mt-4 !bg-white hidden`}
      key={(Math.random() + 1).toString(36).substring(7)}
    >
      Claim
    </Button>,
  ]);

  return (
    <div
      style={{
        height: height ? `${height}px` : "100vh",
      }}
      id="container"
      className="w-screen overflow-x-hidden overflow-y-scroll font-exo text-base bg-[url('/assets/network/airdrop/airdrop-bg.png')] bg-cover bg-no-repeat"
    >
      <PageHead></PageHead>
      <Nav showConnect={true}></Nav>
      <NetworkMenu></NetworkMenu>
      {canAccess ? (
        <div className={`h-[calc(100%-56px)] overflow-y-scroll px-5 pt-[10vh]`}>
          {hasAirdrop && (
            <div className="w-full sm:w-[785px] mx-auto px-5 sm:px-[52px] py-5 sm:py-[60px] mb-20 flex flex-col items-center">
              <div className="text-[48px] font-extrabold leading-[1.1]">
                KAT Airdrop
              </div>

              <div className={`w-full ${isLoading ? "blur-md" : ""}`}>
                <div className="mt-12 w-full rounded-lg shadow-card bg-white p-6 flex flex-col">
                  <div className="bg-[#EBF3FF] pt-10 px-6 pb-6">
                    <div className="flex items-center justify-center">
                      <div className="text-[36px] font-extrabold text-brand2">
                        {KATAmountFormat(
                          basicClaimed
                            ? 0
                            : airdropInfo?.basicClaimableAmount || 0
                        )}
                      </div>
                      <div className="ml-2 font-semibold text-xl">KAT</div>
                    </div>
                    <div className="flex justify-center">
                      {!claimable ? (
                        <Button
                          variant="outline"
                          radius="xl"
                          className={`w-[215px] h-[44px] mt-4`}
                          disabled={true}
                        >
                          {basicClaimed ? "Claimed" : "Claim"}
                        </Button>
                      ) : basicClaimed ? (
                        <Button
                          variant="outline"
                          radius="xl"
                          className={`w-[215px] h-[44px] mt-4`}
                          disabled={true}
                        >
                          Claimed
                        </Button>
                      ) : showRetweet ? (
                        <Button
                          variant="outline"
                          radius="xl"
                          className={`w-[215px] h-[44px] mt-4 !bg-white`}
                          onClick={onRetweet}
                        >
                          Tweet and claim
                        </Button>
                      ) : (
                        <>{btns}</>
                      )}
                    </div>
                    {!claimable && (
                      <div className="mt-4 text-center font-xs text-dark1">
                        Claimable on{" "}
                        {dayjs(airdropOpenDate).format("M/D/YYYY @ hh A")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-6 mt-6">
                    <AirdropDetail airdrop={airdrops.airdrop1}></AirdropDetail>
                    <AirdropDetail airdrop={airdrops.airdrop2}></AirdropDetail>
                  </div>
                </div>
                {hasValidatorAirdrop && (
                  <div className="mt-6 w-full rounded-lg shadow-card bg-white p-6 flex flex-col">
                    <AirdropDetail
                      airdrop={airdrops.validatorAirdrop}
                      withClaimBtn={true}
                      size="sm"
                      claiming={validatorClaimLoading}
                      claimable={false}
                      onClaim={() => {
                        if (validatorClaimLoading || validatorClaimed) {
                          return;
                        }
                        validatorClaim();
                      }}
                    ></AirdropDetail>
                  </div>
                )}

                <div className="mt-6 w-full rounded-lg shadow-card bg-white p-6 flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">
                      Claimer reward from airdrop #2
                    </div>
                    <div className="flex items-center">
                      <Button
                        variant="default"
                        size="xs"
                        radius="xl"
                        className="w-[64px] h-[24px]"
                        disabled
                      >
                        Claim
                      </Button>
                      <div className="flex items-center ml-4">
                        <span className="font-semibold">
                          {KATAmountFormat(
                            airdropInfo?.A2_Claimer_Tokens_locked
                          )}
                        </span>
                        <span className="ml-2">KAT</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray1 mt-3  max-w-[360px]">
                    {`You will be able to unlock this portion after completing
our campaigns in the coming months. Stay tuned!`}
                  </div>
                </div>

                <div className="mt-6 w-full rounded-lg shadow-card bg-white p-6 flex flex-col">
                  <div className="flex items-center justify-between font-semibold">
                    <div>Total amount</div>
                    <div>
                      {KATAmountFormat(totalAmount)}{" "}
                      <span className="font-normal ml-1">KAT</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!hasAirdrop && (
            <div className="flex items-center justify-center mt-24">
              <NotAvailableTip
                title="You don’t have any airdrops"
                desc="You don’t have any airdrops. But don’t worry, keep an eye out for future airdrops."
              ></NotAvailableTip>
            </div>
          )}
        </div>
      ) : (
        <div className="px-10 sm:px-[18vw] pt-[15vh] ">
          <div className="flex flex-col leading-tight sm:w-[384px]">
            <div className="text-5xl sm:text-[60px] font-extrabold">
              KAT Airdrop
            </div>
            <div className="mt-4">
              Connect your wallet to see if you qualify to receive KAT airdrop.
              <a
                className="action text-primary hover:text-primary"
                href="https://medium.com/@karatdao/karatdao-airdrop-update-3c74343fdf01"
                target="_blank"
                rel="noreferrer"
              >
                View airdrop rules 1
              </a>
              <a
                className="action text-primary hover:text-primary ml-2"
                href="https://medium.com/@karatdao/2nd-round-karat-airdrop-rules-97f231de99b1"
                target="_blank"
                rel="noreferrer"
              >
                Airdrop rules 2
              </a>
            </div>
            <Button
              className="w-[229px] h-[44px] mt-12"
              radius="xl"
              onClick={login}
            >
              {address ? "Sign message" : " Connect wallet"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AirdropPage;
